import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

import { BlockTextComponent } from '../../../common/block-text/block-text.component';
import { BaseVariableListBlockComponent } from '../../../common/base-variable-list-block.component';
import { YoutubeHelper } from '../../../../../utils/youtube-helper';
import { VideoFormat, YoutubeFormatHelper } from '../../../../../utils/youtube-format-helper';

@Component({
    selector: 'app-gen-video-youtube-url',
    imports: [CommonModule, BlockTextComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './gen-video-youtube-url.component.html',
    styleUrl: './gen-video-youtube-url.component.scss'
})
export class GenVideoYoutubeUrlComponent extends BaseVariableListBlockComponent {
  protected readonly variablePrefix = 'GenVideoYoutubeUrl';
  protected readonly minVariables = 3;
  readonly maxVariables = 5;

  videoPlaceholder = 'Enter YouTube video link';

  /** CSS aspect-ratio per format, so a row is the shape its badge claims. */
  private static readonly ROW_ASPECT: Record<VideoFormat, string> = {
    '9:16': '9 / 16',
    '16:9': '16 / 9',
    '1:1': '1 / 1',
  };

  // Memoised by URL: the template asks on every change-detection pass.
  private readonly videoIds = new Map<string, string>();

  /** True once the row holds a link we can read a video id out of. */
  hasVideo(key: string): boolean {
    return !!this.videoId(key);
  }

  /**
   * Thumbnail for a row, at the video's own shape. Empty while the format is
   * still being read and when the link resolves to nothing, so the row never
   * paints YouTube's grey placeholder.
   */
  thumbnail(key: string): string {
    const videoId = this.videoId(key);
    return videoId ? YoutubeFormatHelper.thumbnailFor(videoId) : '';
  }

  /** The measured format, or '' while it is still being read. */
  formatLabel(key: string): string {
    return this.reading(key)?.format ?? '';
  }

  /**
   * Set once the link has been read and is not one of the three allowed
   * formats, or resolves to no video at all. A warning, not a block.
   */
  formatWarning(key: string): string {
    const reading = this.reading(key);
    if (!reading || reading.format) {
      return '';
    }
    return reading.missing
      ? 'No video found — check the link is right and the video is public'
      : `${reading.width}x${reading.height} — not 9:16, 16:9 or 1:1`;
  }

  /** Shape for the row's thumbnail; falls back to 16:9 until the format is known. */
  rowAspectRatio(key: string): string {
    const format = this.reading(key)?.format;
    return format
      ? GenVideoYoutubeUrlComponent.ROW_ASPECT[format]
      : GenVideoYoutubeUrlComponent.ROW_ASPECT['16:9'];
  }

  private reading(key: string) {
    const videoId = this.videoId(key);
    return videoId ? YoutubeFormatHelper.readingFor(videoId) : undefined;
  }

  private videoId(key: string): string {
    const url = this.block.variables?.[key] ?? '';
    let cached = this.videoIds.get(url);

    if (cached === undefined) {
      cached = YoutubeHelper.extractVideoId(url) ?? '';
      this.videoIds.set(url, cached);
    }

    return cached;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: string): void {
    if (this.block.variables) {
      this.block.variables[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }
}
